/**
 * xlsx 底层的 zip / styles.xml / theme / sheet 解析（补充 SheetJS 社区版样式解析缺陷）。
 *
 * 背景：SheetJS 社区版（0.18.x）在 cellStyles:true 时有三个样式解析缺陷——
 *   1) cell.s 只被赋为 fill 对象（styles.Fills[fillid]），font/alignment/border 全部丢失；
 *   2) parse_borders 只解析 diagonal，left/right/top/bottom 全被忽略（Borders 全空）；
 *   3) theme/indexed 颜色无法还原为 #RRGGBB。
 * 因此标准 xlsx 文件的完整样式必须自行解析 xl/styles.xml + xl/theme/theme1.xml，
 * 并用 sheet XML 中 <c s="N"> 的原始样式索引关联到 cellXfs。
 *
 * 依赖：fast-xml-parser（已位于 dependencies）、node:zlib（内置）。
 */
import { createRequire } from 'node:module';
import { inflateRawSync } from 'node:zlib';
import { XMLParser } from 'fast-xml-parser';
import type * as XLSX from 'xlsx';
import {
  extractTemplate,
  type ExtractOptions,
  type TemplateSkeleton,
  type ResolvedAlignment,
  type ResolvedBorder,
  type ResolvedBorderPart,
  type ResolvedColor,
  type ResolvedFill,
  type ResolvedFont,
  type ResolvedStyles,
  type ResolvedXf,
} from './extract.js';

export type {
  ResolvedAlignment,
  ResolvedBorder,
  ResolvedBorderPart,
  ResolvedColor,
  ResolvedFill,
  ResolvedFont,
  ResolvedStyles,
  ResolvedXf,
} from './extract.js';

/** 懒加载 xlsx（模块缓存保证只解析一次）。 */
const require = createRequire(import.meta.url);

/* ------------------------------------------------------------------ */
/* zip                                                               */
/* ------------------------------------------------------------------ */

/** 解析 zip 容器（xlsx 即 zip），返回 条目路径 → 解压后内容。 */
export function parseZip(buf: Buffer): Map<string, Buffer> {
  const map = new Map<string, Buffer>();
  let eocd = -1;
  const searchFrom = Math.max(0, buf.length - 22 - 65536);
  for (let i = buf.length - 22; i >= searchFrom; i--) {
    if (buf.readUInt32LE(i) === 0x06054b50) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) throw new Error('不是有效的 zip/xlsx 文件（找不到 EOCD 记录）');
  const count = buf.readUInt16LE(eocd + 10);
  let pos = buf.readUInt32LE(eocd + 16);
  for (let n = 0; n < count; n++) {
    if (buf.readUInt32LE(pos) !== 0x02014b50) throw new Error('zip 中央目录损坏');
    const method = buf.readUInt16LE(pos + 10);
    const compSize = buf.readUInt32LE(pos + 20);
    const nameLen = buf.readUInt16LE(pos + 28);
    const extraLen = buf.readUInt16LE(pos + 30);
    const commentLen = buf.readUInt16LE(pos + 32);
    const localOff = buf.readUInt32LE(pos + 42);
    const name = buf.toString('utf8', pos + 46, pos + 46 + nameLen);
    if (buf.readUInt32LE(localOff) !== 0x04034b50) throw new Error(`zip 本地文件头损坏: ${name}`);
    const lNameLen = buf.readUInt16LE(localOff + 26);
    const lExtraLen = buf.readUInt16LE(localOff + 28);
    const data = buf.subarray(localOff + 30 + lNameLen + lExtraLen, localOff + 30 + lNameLen + lExtraLen + compSize);
    if (method === 0) map.set(name, Buffer.from(data));
    else if (method === 8) map.set(name, inflateRawSync(data));
    else throw new Error(`不支持的 zip 压缩方式: ${name} (method=${method})`);
    pos += 46 + nameLen + extraLen + commentLen;
  }
  return map;
}

/* ------------------------------------------------------------------ */
/* styles.xml / theme1.xml 解析                                        */
/* ------------------------------------------------------------------ */

const xmlParser = new XMLParser({
  ignoreAttributes: false,
  attributeNamePrefix: '',
  textNodeName: '#text',
  parseAttributeValue: false,
  parseTagValue: false,
  removeNSPrefix: true,
  trimValues: true,
});

type Attr = Record<string, string | undefined>;

function asArray<T>(v: T | T[] | undefined): T[] {
  if (v == null) return [];
  return Array.isArray(v) ? v : [v];
}

/** 从解析对象取 color 定义（<color rgb=".." theme=".." indexed=".."/> 或 srgbClr/sysClr）。 */
function pickColor(o: Attr | undefined): ResolvedColor | undefined {
  if (!o) return undefined;
  const c: ResolvedColor = {};
  if (o.rgb) c.rgb = o.rgb;
  if (o.theme !== undefined) c.theme = Number(o.theme);
  if (o.tint !== undefined) c.tint = Number(o.tint);
  if (o.indexed !== undefined) c.indexed = Number(o.indexed);
  // theme1.xml 的 <a:srgbClr val=".."/> / <a:sysClr lastClr=".."/>
  if (o.val) c.rgb = o.val;
  if (o.lastClr) c.rgb = o.lastClr;
  return c;
}

function parseBool(v: unknown): boolean | undefined {
  if (v === undefined) return undefined;
  return v === '1' || v === 'true' || v === true;
}

/** 解析 xl/styles.xml → 完整样式表。 */
export function parseStylesXml(xml: string): ResolvedStyles {
  const root = xmlParser.parse(xml);
  const ss = root.styleSheet ?? root['s:styleSheet'] ?? root;
  const fonts: ResolvedFont[] = asArray(ss.fonts?.font).map((f: Attr & Record<string, unknown>) => ({
    name: typeof f.name === 'object' ? (f.name as Attr).val : undefined,
    sz: typeof f.sz === 'object' ? Number((f.sz as Attr).val) : undefined,
    bold: typeof f.b === 'object' ? parseBool((f.b as Attr).val) : f.b === '' ? true : f.b !== undefined ? parseBool(String(f.b)) : undefined,
    italic: typeof f.i === 'object' ? parseBool((f.i as Attr).val) : f.i === '' ? true : f.i !== undefined ? parseBool(String(f.i)) : undefined,
    underline: typeof f.u === 'object' ? parseBool((f.u as Attr).val) : f.u === '' ? true : f.u !== undefined ? parseBool(String(f.u)) : undefined,
    color: pickColor(typeof f.color === 'object' ? (f.color as Attr) : undefined),
  }));
  const fills: ResolvedFill[] = asArray(ss.fills?.fill).map((f: Record<string, unknown>) => {
    const pf = f.patternFill as Record<string, unknown> | undefined;
    if (!pf || typeof pf !== 'object') return {};
    return {
      patternType: (pf as Attr).patternType,
      fgColor: pickColor(typeof pf.fgColor === 'object' ? (pf.fgColor as Attr) : undefined),
      bgColor: pickColor(typeof pf.bgColor === 'object' ? (pf.bgColor as Attr) : undefined),
    };
  });
  const borders: ResolvedBorder[] = asArray(ss.borders?.border).map((b: Record<string, unknown>) => {
    const out: ResolvedBorder = {};
    for (const side of ['left', 'right', 'top', 'bottom'] as const) {
      const part = b[side] as Record<string, unknown> | undefined;
      if (!part || typeof part !== 'object') continue;
      out[side] = {
        style: (part as Attr).style,
        color: pickColor(typeof part.color === 'object' ? (part.color as Attr) : undefined),
      };
    }
    return out;
  });
  const cellXfs: ResolvedXf[] = asArray(ss.cellXfs?.xf).map((x: Record<string, unknown>) => {
    const xf: ResolvedXf = {
      fontId: (x as Attr).fontId !== undefined ? Number((x as Attr).fontId) : undefined,
      fillId: (x as Attr).fillId !== undefined ? Number((x as Attr).fillId) : undefined,
      borderId: (x as Attr).borderId !== undefined ? Number((x as Attr).borderId) : undefined,
      numFmtId: (x as Attr).numFmtId !== undefined ? Number((x as Attr).numFmtId) : undefined,
    };
    const al = x.alignment as Attr | undefined;
    if (al && typeof al === 'object') {
      xf.alignment = {
        horizontal: al.horizontal,
        vertical: al.vertical,
        wrapText: parseBool(al.wrapText),
      } satisfies ResolvedAlignment;
    }
    return xf;
  });
  return { fonts, fills, borders, cellXfs };
}

/**
 * 解析 xl/theme/theme1.xml → clrScheme 色值数组（按规范顺序
 * dk1, lt1, dk2, lt2, accent1..6, hlink, folHlink，共 12 项；缺项留空）。
 */
export function parseThemeXml(xml: string | undefined): Array<string | undefined> {
  if (!xml) return [];
  const root = xmlParser.parse(xml);
  const theme = root.theme ?? root['a:theme'];
  const scheme = theme?.themeElements?.clrScheme;
  if (!scheme || typeof scheme !== 'object') return [];
  const order = ['dk1', 'lt1', 'dk2', 'lt2', 'accent1', 'accent2', 'accent3', 'accent4', 'accent5', 'accent6', 'hlink', 'folHlink'];
  return order.map((k) => {
    const node = scheme[k] as Record<string, unknown> | undefined;
    if (!node || typeof node !== 'object') return undefined;
    const sys = node.sysClr as Attr | undefined;
    if (sys && sys.lastClr) return sys.lastClr;
    const srgb = node.srgbClr as Attr | undefined;
    if (srgb && srgb.val) return srgb.val;
    return undefined;
  });
}

/** 从 sheet XML 提取 单元格地址(A1) → 原始样式索引 s 的映射。 */
export function parseSheetStyleIndexes(sheetXml: string): Map<string, number> {
  const map = new Map<string, number>();
  const re = /<c\s+[^>]*?>/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(sheetXml))) {
    const tag = m[0];
    const r = /r="([A-Z]+\d+)"/.exec(tag);
    const s = /s="(\d+)"/.exec(tag);
    if (r && s) map.set(r[1]!, Number(s[1]));
  }
  return map;
}

/* ------------------------------------------------------------------ */
/* 一站式：buffer → 骨架                                              */
/* ------------------------------------------------------------------ */

/**
 * 从 xlsx 文件 buffer 直接提取模板骨架（推荐入口，CLI 使用）。
 * 内部：XLSX.read 解析文本/合并/行列尺寸 → 自建完整样式表（styles.xml+theme1.xml）
 *       + sheet XML 样式索引 → 传给 extractTemplate 的索引形态解析。
 */
export function extractTemplateFromBuffer(buf: Buffer, opts: ExtractOptions): TemplateSkeleton {
  const XLSXmod: typeof XLSX = require('xlsx');
  const wb = XLSXmod.read(buf, { type: 'buffer', cellStyles: true });
  const sheetName = opts.sheet && wb.SheetNames.includes(opts.sheet) ? opts.sheet : wb.SheetNames[0]!;
  const sheetIdx = wb.SheetNames.indexOf(sheetName);

  const zip = parseZip(buf);
  const stylesXml = zip.get('xl/styles.xml');
  const themeXml = zip.get('xl/theme/theme1.xml');
  const sheetXml = zip.get(`xl/worksheets/sheet${sheetIdx + 1}.xml`);

  const styles = stylesXml ? parseStylesXml(stylesXml.toString('utf8')) : undefined;
  const themeClrs = themeXml ? parseThemeXml(themeXml.toString('utf8')) : undefined;
  const styleIndex = sheetXml ? parseSheetStyleIndexes(sheetXml.toString('utf8')) : undefined;

  return extractTemplate(wb, {
    ...opts,
    styles,
    themeClrs,
    styleIndex,
  });
}
